import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useChangePassword } from '@/api/hooks/useAccount';
import type { AppError } from '@/api/client';

/**
 * Self-service password change. Three fields: current, new,
 * confirm — Save is disabled until current is non-empty, new is
 * 8+ chars, and new matches confirm. The 8-char minimum mirrors
 * the {@code @Size(min = 8)} on {@code ChangePasswordRequest}.
 *
 * <p>Distinct from the forgotten-password flow (`/forgot-password`)
 * which authenticates via a one-time email token. This one is for
 * a logged-in customer who wants to rotate; we require the current
 * password so a hijacked session can't be used to lock the real
 * owner out.
 */
export default function PasswordSection() {
  const { t } = useTranslation();
  const changePassword = useChangePassword();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const validNew = newPassword.length >= 8;
  const matches = newPassword === confirm;
  const canSubmit = currentPassword.length > 0 && validNew && matches;

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirm('');
    setError(null);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!matches) {
      setError(t('account.password_mismatch'));
      return;
    }
    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          reset();
          toast.success(t('account.password_changed'));
        },
        onError: (err) => {
          const e = err as unknown as AppError;
          // 400 from the server includes the specific message ("current
          // password is incorrect" / "must be different"). Render it
          // verbatim — saves us a translation lookup table for every
          // possible server-side validation.
          setError(e?.message ?? t('common.error'));
        },
      },
    );
  };

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-neutral-500">
        {t('account.password')}
      </h2>
      <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:max-w-md">
        <Field label={t('account.current_password')}>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            className={inputCls}
          />
        </Field>
        <Field label={t('account.new_password')}>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            className={inputCls}
          />
        </Field>
        <Field label={t('account.confirm_password')}>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className={inputCls}
          />
          {confirm.length > 0 && !matches && (
            <p className="mt-1 text-xs text-red-600">{t('account.password_mismatch')}</p>
          )}
        </Field>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={!canSubmit || changePassword.isPending}
          className="w-fit rounded-md bg-[#C2713A] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#a95f2f] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {changePassword.isPending ? t('common.saving') : t('account.change_password')}
        </button>
      </form>
    </section>
  );
}

const inputCls =
  'w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus-visible:border-[#C2713A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A]/30';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-xs font-medium text-neutral-500">{label}</span>
      {children}
    </label>
  );
}
